import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Master from "./Master";
import { batchList } from "../../api/Batch";
import { busList } from "../../api/Transport";
import { GetHostelList } from "../../api/HostelService";
import { librariesList } from "../../api/Library";

import { SweetAlertShow } from "../master/Master";

import {
  studentFeeTransactionsSummaryV2,
  studentFeeTransactionDeleteV4,
  studentManualFeeTransactions,
} from "../../api/Student";
import usePrivateLink from "../config/usePrivateLink";
import { BankList } from "../../api/BankService";
import DownloadExcel from "../master/DownloadExcel";
import { PrintPage } from "../master/Print";
import { formatToDate } from "../master/FormatToDateTime";
import ReceiptNoUpdateModal from "./components/ReceiptNoUpdateModal";
import ManualFeeTransactionsModal from "./components/ManualFeeTransactionsModal";

const FEE_TRANSACTIONS_UI_STYLES = `
.fee-transactions-v2-ui {
  --fee-radius: 16px;
  --fee-border: rgba(100, 116, 139, 0.18);
  --fee-shadow: 0 8px 28px rgba(15, 23, 42, 0.07);
  width: 100%;
}

.fee-transactions-v2-ui .card {
  border: 1px solid var(--fee-border);
  border-radius: var(--fee-radius);
  box-shadow: var(--fee-shadow);
  overflow: hidden;
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
}

.fee-transactions-v2-ui .card:hover {
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.10);
}

.fee-transactions-v2-ui .card-header {
  border-bottom: 1px solid var(--fee-border);
  background: transparent;
}

.fee-transactions-v2-ui .btn {
  border-radius: 10px;
  transition: all .18s ease;
}

.fee-transactions-v2-ui .btn:hover {
  transform: translateY(-1px);
}

.fee-transactions-v2-ui .form-control,
.fee-transactions-v2-ui .form-select {
  min-height: 42px;
  border-radius: 10px;
  border-color: var(--fee-border);
  box-shadow: none;
}

.fee-transactions-v2-ui .form-control:focus,
.fee-transactions-v2-ui .form-select:focus {
  border-color: rgba(99, 102, 241, .55);
  box-shadow: 0 0 0 .2rem rgba(99, 102, 241, .10);
}

.fee-transactions-v2-ui table {
  margin-bottom: 0;
}

.fee-transactions-v2-ui .table > :not(caption) > * > * {
  padding: .78rem .85rem;
  vertical-align: middle;
  border-bottom-color: var(--fee-border);
}

.fee-transactions-v2-ui .table thead th {
  font-size: .74rem;
  font-weight: 700;
  letter-spacing: .035em;
  text-transform: uppercase;
  white-space: nowrap;
}

.fee-transactions-v2-ui .table tbody tr {
  transition: background-color .15s ease;
}

.fee-transactions-v2-ui .table tbody tr:hover {
  background: rgba(99, 102, 241, .045);
}

.fee-transactions-v2-ui .badge {
  border-radius: 999px;
  font-weight: 600;
}

.fee-transactions-v2-ui .progress {
  border-radius: 999px;
}

.fee-transactions-v2-ui .offcanvas {
  border-left: 1px solid var(--fee-border);
}

.fee-transactions-v2-ui .offcanvas-header {
  border-bottom: 1px solid var(--fee-border);
}

.fee-transactions-v2-ui .offcanvas-body {
  scrollbar-width: thin;
}

.fee-transactions-v2-ui .modal-content {
  border: 1px solid var(--fee-border);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(15, 23, 42, .18);
}

.fee-transactions-v2-ui .modal-header {
  border-bottom: 1px solid var(--fee-border);
}

.fee-transactions-v2-ui .modal-footer {
  border-top: 1px solid var(--fee-border);
}

.fee-transactions-v2-ui .bg-primary-subtle,
.fee-transactions-v2-ui .bg-success-subtle,
.fee-transactions-v2-ui .bg-warning-subtle,
.fee-transactions-v2-ui .bg-danger-subtle,
.fee-transactions-v2-ui .bg-info-subtle,
.fee-transactions-v2-ui .bg-secondary-subtle {
  border-radius: 10px;
}

@media (max-width: 767.98px) {
  .fee-transactions-v2-ui .card {
    border-radius: 13px;
  }

  .fee-transactions-v2-ui .table > :not(caption) > * > * {
    padding: .65rem .7rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .fee-transactions-v2-ui *,
  .fee-transactions-v2-ui *::before,
  .fee-transactions-v2-ui *::after {
    transition: none !important;
    animation: none !important;
  }
}

/* Dark themes used by the ERP */
[data-bs-theme="dark"] .fee-transactions-v2-ui,
.dark .fee-transactions-v2-ui,
.dark-mode .fee-transactions-v2-ui {
  --fee-border: rgba(148, 163, 184, 0.16);
  --fee-shadow: 0 10px 30px rgba(0, 0, 0, .18);
}

[data-bs-theme="dark"] .fee-transactions-v2-ui .table tbody tr:hover,
.dark .fee-transactions-v2-ui .table tbody tr:hover,
.dark-mode .fee-transactions-v2-ui .table tbody tr:hover {
  background: rgba(99, 102, 241, .08);
}
`;

/* ============================================================
   FEE TYPE META — single source of truth for color/icon/label
   used by both the summary cards and the fee-module chips in
   the transactions table.
============================================================ */

const FEE_TYPE_META = {
  batch_fee: {
    label: "Batch Fee",
    color: "#6366f1",
    icon: "ri-graduation-cap-line",
  },

  hostel_fee: {
    label: "Hostel Fee",
    color: "#22c55e",
    icon: "ri-hotel-bed-line",
  },

  transport_fee: {
    label: "Transport Fee",
    color: "#f59e0b",
    icon: "ri-bus-line",
  },

  library_fee: {
    label: "Library Fee",
    color: "#06b6d4",
    icon: "ri-book-2-line",
  },

  manual_fee: {
    label: "Manual Fee",
    color: "#ec4899",
    icon: "ri-edit-2-line",
  },

  other_fee: {
    label: "Other Fee",
    color: "#8b5cf6",
    icon: "ri-wallet-3-line",
  },
};

const feeMeta = (key) =>
  FEE_TYPE_META[key] || {
    label: key || "Fee",
    color: "#64748b",
    icon: "ri-price-tag-3-line",
  };

export default function FeesTransactionsV2() {
  const hasAccess = usePrivateLink();
  const [sessions, setSessions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [buses, setBuses] = useState([]);
  const [libraries, setLibraries] = useState([]);
  const [banks, setBanks] = useState([]);

  const [sessionLoading, setSessionLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [hostelLoading, setHostelLoading] = useState(false);
  const [busLoading, setBusLoading] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [bankLoading, setBankLoading] = useState(false);

  const [filter, setFilter] = useState({
    session_id: "",
    batch_id: "",
    hostel_id: "",
    bus_id: "",
    library_id: "",
    bank_id: "",

    fee_type: "",
    other_fee_filter: "",

    payment_from_date: "",
    payment_to_date: "",

    created_from_date: "",
    created_to_date: "",
  });

  useEffect(() => {
    getSessions();
  }, []);

  const getSessions = () => {
    setSessionLoading(true);

    try {
      const storedSessions = JSON.parse(
        localStorage.getItem("sessions") ?? "[]",
      );

      const sessionData = Array.isArray(storedSessions) ? storedSessions : [];

      setSessions(sessionData);

      const activeSession = sessionData.find(
        (item) => Number(item.status) === 1,
      );

      if (!filter?.session_id && activeSession?.id) {
        setFilter((prev) => ({
          ...prev,
          session_id: activeSession.id,
        }));
      }
    } catch (error) {
      console.error("Session error:", error);
      setSessions([]);
    } finally {
      setSessionLoading(false);
    }
  };

  useEffect(() => {
    getBanks();
    getBuses();
  }, []);

  const getBanks = async () => {
    setBankLoading(true);

    try {
      const response = await BankList();

      if (response?.data?.status) {
        setBanks(response?.data?.data || []);
      } else {
        setBanks([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load banks",
        );
      }
    } catch (error) {
      setBanks([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.message ??
        "Unable to load banks",
      );
    } finally {
      setBankLoading(false);
    }
  };

  const getBuses = async () => {
    setBusLoading(true);

    try {
      const response = await busList("");

      if (response?.data?.status) {
        setBuses(response?.data?.data || []);
      } else {
        setBuses([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load buses",
        );
      }
    } catch (error) {
      setBuses([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.message ??
        "Unable to load buses",
      );
    } finally {
      setBusLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Session Dependent Lists
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!filter?.session_id) {
      setBatches([]);
      setHostels([]);
      setLibraries([]);
      return;
    }

    getBatches();
    getHostels();
    getLibraries();
  }, [filter?.session_id]);

  const getBatches = async () => {
    setBatchLoading(true);

    try {
      let addUrl = "";

      if (filter?.session_id) {
        addUrl += `&session_id=${encodeURIComponent(filter.session_id)}`;
      }

      const response = await batchList(addUrl);

      if (response?.data?.status) {
        setBatches(response?.data?.data || []);
      } else {
        setBatches([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load batches",
        );
      }
    } catch (error) {
      setBatches([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.message ??
        "Unable to load batches",
      );
    } finally {
      setBatchLoading(false);
    }
  };

  const getHostels = async () => {
    setHostelLoading(true);

    try {
      const response = await GetHostelList("limit=all");

      if (response?.data?.status) {
        setHostels(response?.data?.data || []);
      } else {
        setHostels([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load hostels",
        );
      }
    } catch (error) {
      setHostels([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.message ??
        "Unable to load hostels",
      );
    } finally {
      setHostelLoading(false);
    }
  };

  const getLibraries = async () => {
    setLibraryLoading(true);

    try {
      let addUrl = "";

      if (filter?.session_id) {
        addUrl += `&session_id=${encodeURIComponent(filter.session_id)}`;
      }

      const response = await librariesList(addUrl);

      if (response?.data?.status) {
        setLibraries(response?.data?.data || []);
      } else {
        setLibraries([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load libraries",
        );
      }
    } catch (error) {
      setLibraries([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.message ??
        "Unable to load libraries",
      );
    } finally {
      setLibraryLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Filter Handlers
  |--------------------------------------------------------------------------
  */

  // const handleChange = (e) => {
  //   const { name, value } = e.target;

  //   setFilter((prev) => ({
  //     ...prev,
  //     [name]: value,
  //   }));
  // };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSessionChange = (e) => {
    const value = e.target.value;

    setFilter((prev) => ({
      ...prev,
      session_id: value,

      batch_id: "",
      hostel_id: "",
      bus_id: "",
      library_id: "",
    }));
  };

  const handleReset = () => {
    const activeSession = sessions.find((item) => Number(item.status) === 1);

    setFilter({
      session_id: activeSession?.id || "",

      batch_id: "",
      hostel_id: "",
      bus_id: "",
      library_id: "",

      bank_id: "",

      fee_type: "",
      other_fee_filter: "",

      payment_from_date: "",
      payment_to_date: "",

      created_from_date: "",
      created_to_date: "",
    });
  };

  const removeFilter = (name) => {
    setFilter((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const selectedSession = sessions.find(
    (item) => String(item.id) === String(filter?.session_id),
  );

  const selectedBatch = batches.find(
    (item) => String(item.id) === String(filter?.batch_id),
  );

  const selectedHostel = hostels.find(
    (item) => String(item.id) === String(filter?.hostel_id),
  );

  const selectedBus = buses.find(
    (item) => String(item.id) === String(filter?.bus_id),
  );

  const selectedLibrary = libraries.find(
    (item) => String(item.id) === String(filter?.library_id),
  );

  const selectedBank = banks.find(
    (item) => String(item.id) === String(filter?.bank_id),
  );

  const hasFilter =
    Boolean(filter?.batch_id) ||
    Boolean(filter?.hostel_id) ||
    Boolean(filter?.bus_id) ||
    Boolean(filter?.library_id) ||
    Boolean(filter?.bank_id) ||
    Boolean(filter?.payment_from_date) ||
    Boolean(filter?.payment_to_date) ||
    Boolean(filter?.created_from_date) ||
    Boolean(filter?.created_to_date);

  /*
  |--------------------------------------------------------------------------
  | Formatting helpers
  |--------------------------------------------------------------------------
  */

  const inr = useCallback((value) => {
    const amount = Number(value || 0);

    return `₹ ${amount.toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;
  }, []);

  const number = useCallback((value) => {
    return Number(value || 0).toLocaleString("en-IN");
  }, []);

  const formatDate = useCallback((value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, []);

  const formatDateTime = useCallback((value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const getPaymentMethodMeta = (method) => {
    const value = String(method || "").toLowerCase();

    if (value === "cash") {
      return { cls: "bg-success-subtle text-success", icon: "ri-cash-line" };
    }

    if (value === "online") {
      return {
        cls: "bg-primary-subtle text-primary",
        icon: "ri-smartphone-line",
      };
    }

    if (value === "cheque") {
      return {
        cls: "bg-warning-subtle text-warning",
        icon: "ri-file-paper-2-line",
      };
    }

    return {
      cls: "bg-secondary-subtle text-secondary",
      icon: "ri-bank-card-line",
    };
  };

  /*
  |--------------------------------------------------------------------------
  | API
  |--------------------------------------------------------------------------
  */

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [error, setError] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const [showManualFeeModal, setShowManualFeeModal] = useState(false);
  const [manualFeeTransactions, setManualFeeTransactions] = useState([]);
  const [manualFeeLoading, setManualFeeLoading] = useState(false);
  const [manualFeeError, setManualFeeError] = useState("");
  const [showOtherFeeModal, setShowOtherFeeModal] = useState(false);
  const [otherFeeTransactions, setOtherFeeTransactions] = useState([]);
  const [otherFeeLoading, setOtherFeeLoading] = useState(false);
  const [otherFeeError, setOtherFeeError] = useState("");

  const getSummary = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      let addUrl = "";

      Object.keys(filter || {}).forEach((key) => {
        const value = filter[key];

        if (value !== undefined && value !== null && value !== "") {
          addUrl += `&${key}=${encodeURIComponent(value)}`;
        }
      });

      const response = await studentFeeTransactionsSummaryV2(addUrl);

      if (response?.data?.status) {
        setData(response?.data?.data || null);
      } else {
        setData(null);

        setError(
          response?.data?.message || "Unable to load fee transaction summary.",
        );
      }
    } catch (err) {
      console.error("Fee transaction summary error:", err);

      setData(null);

      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong while loading transactions.",
      );
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    getSummary();
  }, [getSummary]);

  const getManualFeeTransactions = useCallback(async () => {
    setManualFeeLoading(true);
    setManualFeeError("");

    try {
      const response = await studentManualFeeTransactions({});

      if (response?.data?.status) {
        setManualFeeTransactions(response?.data?.data || []);
      } else {
        setManualFeeTransactions([]);
        setManualFeeError(
          response?.data?.message || "Unable to load manual fee transactions.",
        );
      }
    } catch (error) {
      console.error("Manual fee transactions error:", error);

      setManualFeeTransactions([]);

      setManualFeeError(
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load manual fee transactions.",
      );
    } finally {
      setManualFeeLoading(false);
    }
  }, []);

  const handleManualFeeClick = async () => {
    setShowManualFeeModal(true);
    await getManualFeeTransactions();
  };

  const getOtherFeeTransactions = useCallback(async () => {
    setOtherFeeLoading(true);
    setOtherFeeError("");

    try {
      let addUrl = "";

      Object.keys(filter || {}).forEach((key) => {
        if (key === "fee_type" || key === "other_fee_filter") return;

        const value = filter[key];

        if (value !== undefined && value !== null && value !== "") {
          addUrl += `&${key}=${encodeURIComponent(value)}`;
        }
      });

      addUrl += "&fee_type=other_fee";

      const response = await studentFeeTransactionsSummaryV2(addUrl);

      if (response?.data?.status) {
        const transactionList = Array.isArray(
          response?.data?.data?.transaction_list,
        )
          ? response.data.data.transaction_list
          : [];

        setOtherFeeTransactions(transactionList);
      } else {
        setOtherFeeTransactions([]);
        setOtherFeeError(
          response?.data?.message || "Unable to load other fee transactions.",
        );
      }
    } catch (error) {
      console.error("Other fee transactions error:", error);

      setOtherFeeTransactions([]);

      setOtherFeeError(
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load other fee transactions.",
      );
    } finally {
      setOtherFeeLoading(false);
    }
  }, [filter]);

  const handleOtherFeeClick = async () => {
    setShowOtherFeeModal(true);
    await getOtherFeeTransactions();
  };

  const checkAccess = useCallback((path) => {
    try {
      return typeof hasAccess === "function" ? hasAccess(path) : true;
    } catch (e) {
      return true;
    }
  }, []);

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This Transaction will be permanently deleted. You cannot undo this action.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    setDeleteLoading(id);

    try {
      const response = await studentFeeTransactionDeleteV4(id);

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Successfully",
          response?.data?.message || "Transaction deleted successfully.",
        );

        await getSummary();
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to delete transaction.",
        );
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ??
        err?.response?.message ??
        err?.message ??
        "Something went wrong while deleting transaction.",
      );
    } finally {
      setDeleteLoading(null);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Derived data
  |--------------------------------------------------------------------------
  */

  const summary = data?.summary || {};
  const today = data?.today || {};

  const byPaymentDate = today?.by_payment_date || {};
  const byCreatedAt = today?.by_created_at || {};

  const transactions = useMemo(() => {
    return Array.isArray(data?.transaction_list) ? data.transaction_list : [];
  }, [data]);

  const feeData = useMemo(
    () =>
      Object.keys(FEE_TYPE_META).map((key) => ({
        key,
        ...FEE_TYPE_META[key],
        data: summary?.[key] || {},
      })),
    [summary],
  );

  const showSkeleton = loading && !data;
  const showError = !loading && !!error && !data;

  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  const handleDownload = async (type = "excel") => {
    setLoading(true);

    try {
      const transactionData = Array.isArray(data?.transaction_list)
        ? data.transaction_list
        : [];

      if (!transactionData.length) {
        SweetAlertShow("info", "Info", "No transaction records found.");
        return;
      }

      const downloadData = transactionData.map((item) => {
        const studentName =
          item?.student?.name ??
          item?.library_student?.student_name ??
          item?.hostel_student?.student_name ??
          "-";

        const registrationNo = item?.student?.registration_no ?? "-";

        const moduleText = (() => {
          try {
            const modules =
              typeof item?.module === "string"
                ? JSON.parse(item.module)
                : item?.module || [];

            if (!Array.isArray(modules) || !modules.length) {
              return "-";
            }

            return modules
              .map(
                (module) => `${module?.type || "-"} (${inr(module?.amount)})`,
              )
              .join(", ");
          } catch (error) {
            return item?.module || "-";
          }
        })();

        return {
          "Student Name": studentName,
          "Registration No": registrationNo,
          "Receipt No": item?.receipt_no ?? "-",
          Module: moduleText,
          Amount: Number(item?.paid ?? item?.amount ?? 0),
          "Payment Method": item?.payment_method ?? "-",
          "Bank Account": item?.bank?.account_no ?? "-",
          "Transaction No": item?.transaction_no ?? "-",
          "Payment Date": item?.payment_date
            ? formatToDate(item.payment_date)
            : "-",
          "Created At": item?.created_at
            ? formatDateTime(item.created_at)
            : "-",
          "Created By": item?.created_by?.name ?? "-",
          Phone:
            item?.student?.phone ??
            item?.library_student?.phone ??
            item?.hostel_student?.phone ??
            "-",
          Email:
            item?.student?.email ??
            item?.library_student?.email ??
            item?.hostel_student?.email ??
            "-",
          Remark: item?.remark ?? "-",
        };
      });

      // Add total row
      const totalAmount = transactionData.reduce(
        (total, item) => total + Number(item?.paid ?? item?.amount ?? 0),
        0,
      );

      downloadData.push({
        "Student Name": "",
        "Registration No": "",
        "Receipt No": "",
        Module: "TOTAL",
        Amount: totalAmount,
        "Payment Method": "",
        "Bank Account": "",
        "Transaction No": "",
        "Payment Date": "",
        "Created At": "",
        "Created By": "",
        Phone: "",
        Email: "",
        Remark: "",
      });

      const selectedSessionName =
        selectedSession?.session || selectedSession?.title || "";

      const selectedBatchName =
        selectedBatch?.title || selectedBatch?.name || "";

      let title = "Fee Transactions Report";

      if (selectedSessionName) {
        title += ` - ${selectedSessionName}`;
      }

      if (selectedBatchName) {
        title += ` - ${selectedBatchName}`;
      }

      if (type === "excel") {
        const now = new Date();

        const fileName = `Fee_Transactions_V2_${now.getFullYear()}_${String(
          now.getMonth() + 1,
        ).padStart(2, "0")}_${String(now.getDate()).padStart(2, "0")}_${String(
          now.getHours(),
        ).padStart(2, "0")}_${String(now.getMinutes()).padStart(2, "0")}`;

        DownloadExcel(downloadData, [], fileName, []);
      } else {
        PrintPage(downloadData, title);
      }
    } catch (error) {
      console.error("Fee transaction download error:", error);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.message ??
        "Unable to generate report.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <div className="fee-transactions-v2-ui">
        <style>{FEE_TRANSACTIONS_UI_STYLES}</style>
        <FilterHeader
          filter={filter}
          hasFilter={hasFilter}
          selectedSession={selectedSession}
          selectedBatch={selectedBatch}
          selectedHostel={selectedHostel}
          selectedBus={selectedBus}
          selectedLibrary={selectedLibrary}
          selectedBank={selectedBank}
          removeFilter={removeFilter}
          handleReset={handleReset}
          totalCount={transactions.length}
        />

        <OffcanvasFilters
          sessions={sessions}
          batches={batches}
          hostels={hostels}
          buses={buses}
          libraries={libraries}
          banks={banks}
          sessionLoading={sessionLoading}
          batchLoading={batchLoading}
          hostelLoading={hostelLoading}
          busLoading={busLoading}
          libraryLoading={libraryLoading}
          bankLoading={bankLoading}
          filter={filter}
          handleChange={handleChange}
          handleSessionChange={handleSessionChange}
          handleReset={handleReset}
        />

        {showSkeleton && <LoadingSkeleton />}

        {showError && <ErrorState error={error} onRetry={getSummary} />}

        {!showSkeleton && !showError && (
          <>
            {loading && (
              <div className="col-12 mb-3">
                <div
                  className="progress"
                  style={{ height: "3px", overflow: "hidden" }}
                >
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    style={{ width: "100%" }}
                  />
                </div>
              </div>
            )}

            <div className="col-12">
              <div className="row g-3 mb-3">
                <TodayCard
                  title="By Payment Date"
                  subtitle="Today's payments"
                  icon="ri-calendar-check-line"
                  color="#10b981"
                  amount={inr(byPaymentDate?.amount)}
                  transactions={number(byPaymentDate?.transactions)}
                />

                <TodayCard
                  title="By Created Date"
                  subtitle="Transactions created today"
                  icon="ri-time-line"
                  color="#0ea5e9"
                  amount={inr(byCreatedAt?.amount)}
                  transactions={number(byCreatedAt?.transactions)}
                />
              </div>

              <div className="row g-3 mb-3">
                <SummaryCard
                  title="Total Students"
                  value={number(summary?.total_students)}
                  icon="ri-group-line"
                  color="#4f46e5"
                />

                <SummaryCard
                  title="Total Transactions"
                  value={number(summary?.total_transactions)}
                  icon="ri-exchange-funds-line"
                  color="#0ea5e9"
                />

                <SummaryCard
                  title="Total Collection"
                  value={inr(summary?.total_amount)}
                  icon="ri-money-rupee-circle-line"
                  color="#10b981"
                />
              </div>

              <div className="row g-3 mb-3">
                {feeData.map((item) => (
                  <FeeCard
                    key={item.key}
                    item={item}
                    inr={inr}
                    number={number}
                    onClick={
                      item.key === "manual_fee"
                        ? handleManualFeeClick
                        : item.key === "other_fee"
                          ? handleOtherFeeClick
                          : undefined
                    }
                  />
                ))}
              </div>
            </div>

            <TransactionsTable
              transactions={transactions}
              inr={inr}
              formatDate={formatDate}
              formatDateTime={formatDateTime}
              getPaymentMethodMeta={getPaymentMethodMeta}
              checkAccess={checkAccess}
              handleDelete={handleDelete}
              deleteLoading={deleteLoading}
              hasAccess={hasAccess}
              handleDownload={handleDownload}
              loading={loading}
              setSelectedTransaction={setSelectedTransaction}
            />
            {selectedTransaction && (
              <ReceiptNoUpdateModal
                transaction={selectedTransaction}
                onClose={() => setSelectedTransaction(null)}
                onUpdated={() => {
                  setSelectedTransaction(null);
                  getSummary();
                }}
              />
            )}
            {showManualFeeModal && (
              <ManualFeeTransactionsModal
                transactions={manualFeeTransactions}
                loading={manualFeeLoading}
                error={manualFeeError}
                onClose={() => setShowManualFeeModal(false)}
                inr={inr}
                formatDate={formatDate}
                formatDateTime={formatDateTime}
                onRetry={getManualFeeTransactions}
              />
            )}
            {showOtherFeeModal && (
              <OtherFeeTransactionsModal
                transactions={otherFeeTransactions}
                loading={otherFeeLoading}
                error={otherFeeError}
                onClose={() => setShowOtherFeeModal(false)}
                inr={inr}
                formatDate={formatDate}
                formatDateTime={formatDateTime}
                getPaymentMethodMeta={getPaymentMethodMeta}
                onRetry={getOtherFeeTransactions}
              />
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
}

/* ============================================================
   PAGE LAYOUT
============================================================ */

function PageLayout({ children }) {
  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <Header />

        <div className="layout-page">
          <Nav />

          <div className="content-wrapper">
            <div className="container-fluid flex-grow-1 container-p-y">
              <div className="row g-1">
                <Master />

                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   FILTER BADGE
============================================================ */

function FilterBadge({
  show,
  label,
  value,
  color = "primary",
  filterName,
  onRemove,
}) {
  if (!show) return null;

  return (
    <span
      className={`badge rounded-pill bg-${color}-subtle text-${color} px-3 py-2 d-inline-flex align-items-center gap-1`}
    >
      {label}: {value}
      <i
        className="ri-close-line ms-1"
        style={{ cursor: "pointer" }}
        onClick={() => onRemove(filterName)}
      ></i>
    </span>
  );
}

/* ============================================================
   FILTER HEADER
============================================================ */

function FilterHeader({
  filter,
  hasFilter,
  selectedSession,
  selectedBatch,
  selectedHostel,
  selectedBus,
  selectedLibrary,
  selectedBank,
  removeFilter,
  handleReset,
  totalCount,
}) {
  return (
    <div className="col-12">
      <div className="card border-0 shadow-sm rounded-4 p-3 mb-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div
              className="bg-primary text-white rounded-3 d-flex align-items-center justify-content-center fs-5"
              style={{ width: 46, height: 46 }}
            >
              <i className="ri-money-rupee-circle-line"></i>
            </div>

            <div>
              <h5 className="m-0 fw-bold">Fee Transactions</h5>
              <small className="text-muted">
                <i className="ri-receipt-line me-1"></i>
                {totalCount} record{totalCount === 1 ? "" : "s"} found
              </small>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <div className="d-flex flex-wrap gap-2 align-items-center">
              {filter?.session_id && (
                <FilterBadge
                  show
                  label="Session"
                  value={
                    selectedSession?.session ||
                    selectedSession?.title ||
                    `Session ${filter.session_id}`
                  }
                  color="primary"
                  filterName="session_id"
                  onRemove={removeFilter}
                />
              )}

              {filter?.batch_id && (
                <FilterBadge
                  show
                  label="Batch"
                  value={
                    selectedBatch?.title ||
                    selectedBatch?.name ||
                    `Batch ${filter.batch_id}`
                  }
                  color="success"
                  filterName="batch_id"
                  onRemove={removeFilter}
                />
              )}

              {filter?.hostel_id && (
                <FilterBadge
                  show
                  label="Hostel"
                  value={
                    selectedHostel?.title ||
                    selectedHostel?.name ||
                    selectedHostel?.hostel_name ||
                    `Hostel ${filter.hostel_id}`
                  }
                  color="warning"
                  filterName="hostel_id"
                  onRemove={removeFilter}
                />
              )}

              {filter?.bus_id && (
                <FilterBadge
                  show
                  label="Bus"
                  value={
                    selectedBus?.name ||
                    selectedBus?.bus_name ||
                    selectedBus?.bus_number ||
                    selectedBus?.vehicle_no ||
                    `Bus ${filter.bus_id}`
                  }
                  color="info"
                  filterName="bus_id"
                  onRemove={removeFilter}
                />
              )}

              {filter?.library_id && (
                <FilterBadge
                  show
                  label="Library"
                  value={
                    selectedLibrary?.title ||
                    selectedLibrary?.name ||
                    selectedLibrary?.library_name ||
                    `Library ${filter.library_id}`
                  }
                  color="secondary"
                  filterName="library_id"
                  onRemove={removeFilter}
                />
              )}

              {filter?.bank_id && (
                <FilterBadge
                  show
                  label="Bank"
                  value={
                    selectedBank?.account_no ||
                    selectedBank?.name ||
                    `Bank ${filter.bank_id}`
                  }
                  color="warning"
                  filterName="bank_id"
                  onRemove={removeFilter}
                />
              )}

              {filter?.payment_from_date && (
                <FilterBadge
                  show
                  label="Payment From"
                  value={filter.payment_from_date}
                  color="info"
                  filterName="payment_from_date"
                  onRemove={removeFilter}
                />
              )}

              {filter?.payment_to_date && (
                <FilterBadge
                  show
                  label="Payment To"
                  value={filter.payment_to_date}
                  color="info"
                  filterName="payment_to_date"
                  onRemove={removeFilter}
                />
              )}

              {filter?.created_from_date && (
                <FilterBadge
                  show
                  label="Created From"
                  value={filter.created_from_date}
                  color="success"
                  filterName="created_from_date"
                  onRemove={removeFilter}
                />
              )}

              {filter?.created_to_date && (
                <FilterBadge
                  show
                  label="Created To"
                  value={filter.created_to_date}
                  color="success"
                  filterName="created_to_date"
                  onRemove={removeFilter}
                />
              )}
            </div>

            {hasFilter && (
              <button
                type="button"
                title="Reset Filters"
                className="btn btn-light border rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                style={{ width: 40, height: 40 }}
                onClick={handleReset}
              >
                <i className="ri-refresh-line text-danger"></i>
              </button>
            )}

            <button
              type="button"
              title="Open Filters"
              className="btn btn-light border rounded-circle shadow-sm d-flex align-items-center justify-content-center"
              data-bs-toggle="offcanvas"
              data-bs-target="#feeTransactionFilterCanvas"
              style={{ width: 40, height: 40 }}
            >
              <i className="ri-filter-3-line text-primary"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   OFFCANVAS FILTERS
============================================================ */

function OffcanvasFilters({
  sessions,
  batches,
  hostels,
  buses,
  libraries,
  banks,
  sessionLoading,
  batchLoading,
  hostelLoading,
  busLoading,
  libraryLoading,
  bankLoading,
  filter,
  handleChange,
  handleSessionChange,
  handleReset,
}) {
  const LoadingIcon = () => (
    <span
      className="spinner-border spinner-border-sm"
      role="status"
      aria-hidden="true"
    ></span>
  );

  return (
    <div
      className="offcanvas offcanvas-end"
      tabIndex="-1"
      id="feeTransactionFilterCanvas"
    >
      <div className="offcanvas-header border-bottom">
        <div>
          <h5 className="offcanvas-title mb-0">
            <i className="ri-filter-3-line me-2 text-primary"></i>
            Filters
          </h5>
          <small className="text-muted">Refine fee transaction results</small>
        </div>

        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="offcanvas"
        ></button>
      </div>

      <div className="offcanvas-body">
        <div className="row g-3">
          <div className="col-12">
            <div className="form-floating form-floating-outline">
              <select
                className="form-select"
                name="session_id"
                value={filter?.session_id || ""}
                onChange={handleSessionChange}
                disabled={sessionLoading}
              >
                <option value="">Select Session</option>
                {sessions?.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.session || item.title || `Session ${item.id}`}
                    {Number(item.status) === 1 ? " : Active" : " : Inactive"}
                  </option>
                ))}
              </select>
              <label>
                <i className="ri-calendar-check-line me-1"></i>
                Session
              </label>
              {sessionLoading && (
                <div className="position-absolute top-50 end-0 translate-middle-y me-4">
                  <LoadingIcon />
                </div>
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="form-floating form-floating-outline">
              <select
                className="form-select"
                name="batch_id"
                value={filter?.batch_id || ""}
                onChange={handleChange}
                disabled={batchLoading || !filter?.session_id}
              >
                <option value="">All Batches</option>
                {batches?.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title || item.name || `Batch ${item.id}`}
                    {item.course?.title ? ` (${item.course.title})` : ""}
                  </option>
                ))}
              </select>
              <label>
                <i className="ri-graduation-cap-line me-1"></i>
                Batch
              </label>
              {batchLoading && (
                <div className="position-absolute top-50 end-0 translate-middle-y me-4">
                  <LoadingIcon />
                </div>
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="form-floating form-floating-outline">
              <select
                className="form-select"
                name="hostel_id"
                value={filter?.hostel_id || ""}
                onChange={handleChange}
                disabled={hostelLoading || !filter?.session_id}
              >
                <option value="">All Hostels</option>
                {hostels?.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title ||
                      item.name ||
                      item.hostel_name ||
                      `Hostel ${item.id}`}
                  </option>
                ))}
              </select>
              <label>
                <i className="ri-home-4-line me-1"></i>
                Hostel
              </label>
              {hostelLoading && (
                <div className="position-absolute top-50 end-0 translate-middle-y me-4">
                  <LoadingIcon />
                </div>
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="form-floating form-floating-outline">
              <select
                className="form-select"
                name="bus_id"
                value={filter?.bus_id || ""}
                onChange={handleChange}
                disabled={busLoading || !filter?.session_id}
              >
                <option value="">All Buses</option>
                {buses?.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.bus_number ||
                      item.bus_name ||
                      item.name ||
                      item.vehicle_no ||
                      `Bus ${item.id}`}
                    {item.driver_name ? ` - ${item.driver_name}` : ""}
                  </option>
                ))}
              </select>
              <label>
                <i className="ri-bus-line me-1"></i>
                Bus
              </label>
              {busLoading && (
                <div className="position-absolute top-50 end-0 translate-middle-y me-4">
                  <LoadingIcon />
                </div>
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="form-floating form-floating-outline">
              <select
                className="form-select"
                name="library_id"
                value={filter?.library_id || ""}
                onChange={handleChange}
                disabled={libraryLoading || !filter?.session_id}
              >
                <option value="">All Libraries</option>
                {libraries?.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title ||
                      item.name ||
                      item.library_name ||
                      `Library ${item.id}`}
                  </option>
                ))}
              </select>
              <label>
                <i className="ri-book-2-line me-1"></i>
                Library
              </label>
              {libraryLoading && (
                <div className="position-absolute top-50 end-0 translate-middle-y me-4">
                  <LoadingIcon />
                </div>
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="form-floating form-floating-outline">
              <select
                className="form-select"
                name="bank_id"
                value={filter?.bank_id || ""}
                onChange={handleChange}
                disabled={bankLoading}
              >
                <option value="">All Banks</option>
                {banks?.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.account_no ||
                      item.name ||
                      item.bank_name ||
                      `Bank ${item.id}`}
                    {item.owner_name ? ` (${item.owner_name})` : ""}
                  </option>
                ))}
              </select>
              <label>
                <i className="ri-bank-line me-1"></i>
                Bank Account
              </label>
              {bankLoading && (
                <div className="position-absolute top-50 end-0 translate-middle-y me-4">
                  <LoadingIcon />
                </div>
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="form-floating form-floating-outline">
              <select
                className="form-select"
                name="fee_type"
                value={filter?.fee_type || ""}
                onChange={handleChange}
              >
                <option value="">All Fee Types</option>
                <option value="batch_fee">Batch Fee</option>
                <option value="hostel_fee">Hostel Fee</option>
                <option value="transport_fee">Transport Fee</option>
                <option value="library_fee">Library Fee</option>
                <option value="manual_fee">Manual Fee</option>
                <option value="other_fee">Other Fee</option>
              </select>
              <label>
                <i className="ri-wallet-3-line me-1"></i>
                Fee Type
              </label>
            </div>
          </div>

          {filter?.fee_type === "manual_fee" && (
            <div className="col-12">
              <div className="alert alert-pink d-flex align-items-center gap-2 mb-0">
                <i className="ri-edit-2-line"></i>
                <span>
                  Showing <strong>Manual Fee</strong> transactions.
                </span>
              </div>
            </div>
          )}

          <div className="col-12 mt-4">
            <div className="d-flex align-items-center">
              <i className="ri-money-rupee-circle-line text-primary me-2"></i>
              <span className="fw-semibold">Payment Date</span>
              <div className="flex-grow-1 border-top ms-3"></div>
            </div>
          </div>

          <div className="col-12">
            <div className="form-floating form-floating-outline">
              <input
                type="date"
                className="form-control"
                name="payment_from_date"
                value={filter?.payment_from_date || ""}
                onChange={handleChange}
              />
              <label>Payment Date From</label>
            </div>
          </div>

          <div className="col-12">
            <div className="form-floating form-floating-outline">
              <input
                type="date"
                className="form-control"
                name="payment_to_date"
                value={filter?.payment_to_date || ""}
                onChange={handleChange}
              />
              <label>Payment Date To</label>
            </div>
          </div>

          <div className="col-12 mt-4">
            <div className="d-flex align-items-center">
              <i className="ri-time-line text-info me-2"></i>
              <span className="fw-semibold">Created Date</span>
              <div className="flex-grow-1 border-top ms-3"></div>
            </div>
          </div>

          <div className="col-12">
            <div className="form-floating form-floating-outline">
              <input
                type="date"
                className="form-control"
                name="created_from_date"
                value={filter?.created_from_date || ""}
                onChange={handleChange}
              />
              <label>Created Date From</label>
            </div>
          </div>

          <div className="col-12">
            <div className="form-floating form-floating-outline">
              <input
                type="date"
                className="form-control"
                name="created_to_date"
                value={filter?.created_to_date || ""}
                onChange={handleChange}
              />
              <label>Created Date To</label>
            </div>
          </div>

          <div className="col-12 mt-4">
            <div className="row g-2">
              <div className="col-6">
                <button
                  type="button"
                  className="btn btn-primary w-100"
                  data-bs-dismiss="offcanvas"
                >
                  <i className="ri-check-line me-1"></i>
                  Apply
                </button>
              </div>

              <div className="col-6">
                <button
                  type="button"
                  className="btn btn-outline-danger w-100"
                  onClick={handleReset}
                >
                  <i className="ri-refresh-line me-1"></i>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   TODAY CARD
============================================================ */

function TodayCard({ title, subtitle, icon, color, amount, transactions }) {
  return (
    <div className="col-xl-6 col-lg-6">
      <div
        className="card border-0 shadow-sm h-100"
        style={{ borderLeft: `4px solid ${color}`, borderRadius: "12px" }}
      >
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center"
                style={{
                  width: 48,
                  height: 48,
                  background: `${color}15`,
                  color,
                }}
              >
                <i className={`${icon} fs-4`} />
              </div>

              <div>
                <div className="fw-semibold">{title}</div>
                <small className="text-muted">{subtitle}</small>
              </div>
            </div>

            <div className="text-end">
              <div className="fw-bold fs-5" style={{ color }}>
                {amount}
              </div>
              <small className="text-muted">{transactions} transactions</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   SUMMARY CARD
============================================================ */

function SummaryCard({ title, value, icon, color }) {
  return (
    <div className="col-xl-4 col-lg-4 col-md-4 col-sm-12">
      <div
        className="card border-0 shadow-sm h-100"
        style={{ borderRadius: "12px", borderTop: `3px solid ${color}` }}
      >
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <small className="text-muted d-block mb-1">{title}</small>
              <h4 className="fw-bold mb-0">{value}</h4>
            </div>

            <div
              className="rounded-3 d-flex align-items-center justify-content-center"
              style={{ width: 48, height: 48, background: `${color}15`, color }}
            >
              <i className={`${icon} fs-4`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   FEE CARD
============================================================ */

function FeeCard({ item, inr, number, onClick }) {
  const fee = item.data || {};
  const isClickable = typeof onClick === "function";

  return (
    <div className="col-xl-4 col-lg-4 col-md-6 col-12">
      <div
        className="card border-0 shadow-sm"
        onClick={onClick}
        style={{
          borderRadius: "14px",
          cursor: isClickable ? "pointer" : "default",
          borderLeft: `4px solid ${item.color}`,
          transition: "all .2s ease",
        }}
        onMouseEnter={(e) => {
          if (isClickable) {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,.10)";
          }
        }}
        onMouseLeave={(e) => {
          if (isClickable) {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "";
          }
        }}
      >
        <div className="card-body py-3 px-3">
          {/* HEADER */}
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center"
                style={{
                  width: 40,
                  height: 40,
                  background: `${item.color}12`,
                  color: item.color,
                  flexShrink: 0,
                }}
              >
                <i className={`${item.icon} fs-5`} />
              </div>

              <div>
                <div className="text-muted" style={{ fontSize: "11px" }}>
                  Fee Type
                </div>

                <div
                  className="fw-semibold"
                  style={{
                    fontSize: "15px",
                    color: "#555",
                    lineHeight: 1.2,
                  }}
                >
                  {item.label}
                </div>
              </div>
            </div>

            {isClickable && (
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: 28,
                  height: 28,
                  background: `${item.color}12`,
                  color: item.color,
                }}
              >
                <i className="ri-arrow-right-line" />
              </div>
            )}
          </div>

          {/* COLLECTION */}
          <div className="mt-2">
            <div
              className="fw-bold"
              style={{
                fontSize: "20px",
                color: item.color,
                lineHeight: 1.2,
              }}
            >
              {inr(fee?.amount)}
            </div>
          </div>

          {/* STATS */}
          <div
            className="d-flex align-items-center gap-3 mt-2 pt-2"
            style={{
              borderTop: "1px solid rgba(0,0,0,.06)",
            }}
          >
            <div className="d-flex align-items-center gap-1">
              <i
                className="ri-group-line"
                style={{
                  color: item.color,
                  fontSize: 16,
                }}
              />

              <span className="fw-semibold" style={{ fontSize: "13px" }}>
                {number(fee?.students)}
              </span>

              <span className="text-muted" style={{ fontSize: "11px" }}>
                Students
              </span>
            </div>

            <div
              style={{
                width: 1,
                height: 16,
                background: "#ddd",
              }}
            />

            <div className="d-flex align-items-center gap-1">
              <i
                className="ri-exchange-line"
                style={{
                  color: item.color,
                  fontSize: 16,
                }}
              />

              <span className="fw-semibold" style={{ fontSize: "13px" }}>
                {number(fee?.transactions)}
              </span>

              <span className="text-muted" style={{ fontSize: "11px" }}>
                Txn.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   FEE MODULE CHIP — used inline in both table + mobile card
============================================================ */

function FeeModuleChip({ module, inr }) {
  const meta = feeMeta(module?.fee_type);

  return (
    <span
      className="d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1 mb-1 me-1"
      style={{
        background: `${meta.color}15`,
        color: meta.color,
        fontSize: "0.75rem",
      }}
      title={`${module?.type || meta.label} • IDs: ${module?.ids || "-"}`}
    >
      <i className={meta.icon}></i>
      <span className="fw-semibold">{module?.type || meta.label}</span>
      <span>{inr(module?.amount)}</span>
    </span>
  );
}

/* ============================================================
   TRANSACTIONS TABLE (all data inline, no show/hide toggle)
============================================================ */

function TransactionsTable({
  transactions,
  inr,
  formatDate,
  formatDateTime,
  getPaymentMethodMeta,
  checkAccess,
  handleDelete,
  deleteLoading,
  hasAccess,
  loading,
  handleDownload,
  setSelectedTransaction,
}) {
  return (
    <div className="col-12">
      <div
        className="card border-0 shadow-sm"
        style={{ borderRadius: "12px", overflow: "hidden" }}
      >
        <div className="card-header bg-white py-3">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center"
                style={{
                  width: 42,
                  height: 42,
                  background: "#6366f115",
                  color: "#6366f1",
                }}
              >
                <i className="ri-receipt-line fs-5" />
              </div>

              <div>
                <h5 className="mb-0">Fee Transactions</h5>
                <small className="text-muted">
                  {transactions.length} records
                </small>
              </div>
            </div>

            <div className="d-flex gap-2">
              <span className="badge bg-light text-dark border fw-semibold px-3 py-2 rounded-pill">
                <i className="ri-list-check me-1"></i>
                {transactions.length} record
                {transactions.length === 1 ? "" : "s"}
              </span>

              {hasAccess("/fees/excel/list") && (
                <button
                  type="button"
                  className="btn btn-outline-primary rounded-3"
                  onClick={() => handleDownload("excel")}
                  disabled={loading || !transactions.length}
                  title="Download Excel"
                >
                  <i className="ri-file-excel-2-line me-2"></i>
                  Excel
                </button>
              )}

              {hasAccess("/fees/print/list") && (
                <button
                  type="button"
                  className="btn btn-outline-info rounded-3"
                  onClick={() => handleDownload("print")}
                  disabled={loading || !transactions.length}
                  title="Print Transactions"
                >
                  <i className="ri-printer-line me-2"></i>
                  Print
                </button>
              )}

            </div>
          </div>
        </div>

        {transactions.length === 0 ? (
          <EmptyTransactions />
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="px-3">#</th>
                    <th>
                      <i className="ri-user-3-line me-1"></i>Student
                    </th>
                    <th>
                      <i className="ri-file-list-2-line me-1"></i>Receipt
                    </th>
                    <th>
                      <i className="ri-price-tag-3-line me-1"></i>Fee Modules
                    </th>
                    <th>
                      <i className="ri-money-rupee-circle-line me-1"></i>Amount
                    </th>
                    <th>
                      <i className="ri-bank-line me-1"></i>Bank / Account
                    </th>
                    <th>
                      <i className="ri-chat-3-line me-1"></i>Remark
                    </th>
                    <th>
                      <i className="ri-calendar-check-line me-1"></i>Payment
                      Date
                    </th>
                    <th>
                      <i className="ri-time-line me-1"></i>Created
                    </th>
                    <th className="text-end pe-3">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {transactions.map((item, index) => {
                    const payMeta = getPaymentMethodMeta(item.payment_method);
                    const modules = Array.isArray(item.show_modules)
                      ? item.show_modules
                      : [];

                    const StudentInfo = () => (
                      <div>
                        <div className="fw-semibold">
                          {item?.student?.name ??
                            item?.library_student?.student_name ??
                            item?.hostel_student?.student_name ??
                            "-"}
                        </div>

                        <small className="text-muted d-block">
                          <i className="ri-hashtag me-1"></i>
                          {item?.student?.registration_no || "-"}
                        </small>

                        {item?.student?.father_name && (
                          <small className="text-muted d-block">
                            <i className="ri-user-line me-1"></i>
                            {item.student.father_name}
                          </small>
                        )}

                        {item?.student?.phone && (
                          <small className="text-muted d-block">
                            <i className="ri-phone-line me-1"></i>
                            {item.student.phone}
                          </small>
                        )}
                      </div>
                    );
                    return (
                      <tr key={item.id}>
                        <td className="px-3">
                          <span className="text-muted">{index + 1}</span>
                        </td>

                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {hasAccess("/student/details") ? (
                              <Link
                                to={`/student/details?id=${item?.student?.id}`}
                                className="text-decoration-none"
                              >
                                <StudentInfo item={item} />
                              </Link>
                            ) : (
                              <StudentInfo item={item} />
                            )}
                          </div>
                        </td>

                        <td>
                          <div className="d-flex align-items-center gap-1">
                            <span className="fw-semibold text-nowrap">
                              {item.receipt_no}
                            </span>
                            {hasAccess(
                              "/student/fee/transaction/receipt-no/update",
                            ) && (
                                <i
                                  className="ri-edit-line cursor-pointer text-info"
                                  title="Update Receipt No"
                                  onClick={() => setSelectedTransaction(item)}
                                ></i>
                              )}
                          </div>
                          <span
                            className={`badge rounded-pill mt-1 ${payMeta.cls}`}
                          >
                            <i className={`${payMeta.icon} me-1`} />
                            {item.payment_method || "-"}
                          </span>
                          <small className="text-muted d-block mt-1">
                            <i className="ri-hashtag me-1"></i>
                            {item.transaction_no || "-"}
                          </small>
                          {item.created_by?.name && (
                            <small className="text-muted d-block">
                              <i className="ri-user-star-line me-1"></i>
                              {item.created_by.name}
                            </small>
                          )}
                        </td>

                        <td style={{ minWidth: 220 }}>
                          {modules.length === 0 ? (
                            <span className="text-muted">-</span>
                          ) : (
                            <div className="d-flex flex-wrap">
                              {modules.map((module, i) => (
                                <FeeModuleChip
                                  key={i}
                                  module={module}
                                  inr={inr}
                                />
                              ))}
                            </div>
                          )}
                        </td>

                        <td>
                          <strong className="text-success">
                            {inr(item.amount)}
                          </strong>
                        </td>

                        <td>
                          <div className="fw-medium">
                            <i className="ri-bank-line me-1 text-muted"></i>
                            {item.bank?.owner_name ||
                              item.bank?.account_no ||
                              "-"}
                          </div>
                          <small className="text-muted">
                            {item.account_no || "-"}
                          </small>
                        </td>

                        <td style={{ maxWidth: 180 }}>
                          <span className="text-break">
                            {item.remark || "-"}
                          </span>
                        </td>

                        <td>
                          <div className="fw-medium">
                            {formatDate(item.payment_date)}
                          </div>
                        </td>

                        <td>
                          <small className="text-muted">
                            {formatDateTime(item.created_at)}
                          </small>
                        </td>

                        <td>
                          <div className="d-flex justify-content-end gap-1">
                            {hasAccess("/student/fees/receipt") && (
                              <Link
                                className="btn btn-sm btn-label-primary"
                                to={`/student/fees/receipt?id=${item?.id}`}
                                title="Print Receipt"
                              >
                                <i className="ri-printer-line" />
                              </Link>
                            )}

                            {hasAccess("/student/fee/transaction/delete") && (
                              <button
                                type="button"
                                className="btn btn-sm btn-label-danger"
                                onClick={() => handleDelete(item?.id)}
                                title="Delete Transaction"
                                disabled={deleteLoading === item.id}
                              >
                                {deleteLoading === item.id ? (
                                  <span className="spinner-border spinner-border-sm" />
                                ) : (
                                  <i className="ri-delete-bin-line" />
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   EMPTY STATE (shown inside the table card, e.g. no results)
============================================================ */

function EmptyTransactions() {
  return (
    <div className="py-5 text-center">
      <div
        className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
        style={{
          width: 70,
          height: 70,
          background: "#f1f5f9",
          color: "#64748b",
        }}
      >
        <i className="ri-receipt-line fs-2" />
      </div>

      <h6 className="fw-bold mb-1">No Transactions Found</h6>
      <p className="text-muted mb-0">
        No fee transactions match the selected filters.
      </p>
    </div>
  );
}

/* ============================================================
   ERROR STATE
============================================================ */

function ErrorState({ error, onRetry }) {
  return (
    <div className="col-12">
      <div className="card border-0 shadow-sm" style={{ borderRadius: "12px" }}>
        <div className="card-body text-center py-5">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
            style={{
              width: 65,
              height: 65,
              background: "#fee2e2",
              color: "#dc2626",
            }}
          >
            <i className="ri-error-warning-line fs-2" />
          </div>

          <h5 className="fw-bold">Unable to Load Transactions</h5>
          <p className="text-muted mb-3">{error}</p>

          <button type="button" className="btn btn-primary" onClick={onRetry}>
            <i className="ri-refresh-line me-1" />
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   LOADING SKELETON — an "empty box" placeholder shown while
   the summary + transaction list are first fetched.
============================================================ */

function SkeletonBox({ height }) {
  return (
    <div
      className="card border-0 shadow-sm"
      style={{ height, borderRadius: "12px", background: "#f8fafc" }}
    >
      <div className="card-body d-flex align-items-center justify-content-center h-100">
        <div className="placeholder-glow w-100">
          <span className="placeholder col-4 mb-3 d-block" />
          <span className="placeholder col-8 d-block mb-2" />
          <span className="placeholder col-5 d-block mb-2" />
          <span className="placeholder col-6 d-block" />
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <>
      <div className="row g-3 mb-3">
        {Array.from({ length: 2 }).map((_, index) => (
          <div className="col-xl-6 col-lg-6" key={index}>
            <SkeletonBox height={100} />
          </div>
        ))}
      </div>

      <div className="row g-3 mb-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div className="col-xl-4 col-lg-4 col-md-4" key={index}>
            <SkeletonBox height={105} />
          </div>
        ))}
      </div>

      <div className="row g-3 mb-3">
        {Array.from({ length: 5 }).map((_, index) => (
          <div className="col-xl col-lg-4 col-md-6" key={index}>
            <SkeletonBox height={190} />
          </div>
        ))}
      </div>

      <div className="col-12">
        <div
          className="card border-0 shadow-sm"
          style={{ borderRadius: "12px" }}
        >
          <div className="card-body py-5 text-center">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
              style={{
                width: 60,
                height: 60,
                background: "#eef2ff",
                color: "#6366f1",
              }}
            >
              <span
                className="spinner-border"
                role="status"
                aria-hidden="true"
              ></span>
            </div>
            <h6 className="fw-bold mb-1">Loading Transactions…</h6>
            <p className="text-muted mb-0">
              Fetching the latest fee collection data.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

/* ============================================================
   OTHER FEE TRANSACTIONS MODAL
============================================================ */

function OtherFeeTransactionsModal({
  transactions = [],
  loading,
  error,
  onClose,
  inr,
  formatDate,
  formatDateTime,
  getPaymentMethodMeta,
  onRetry,
}) {
  const getModules = (item) => {
    if (Array.isArray(item?.show_modules)) {
      return item.show_modules.filter(
        (module) => module?.fee_type === "other_fee",
      );
    }

    try {
      const modules =
        typeof item?.module === "string"
          ? JSON.parse(item.module)
          : item?.module || [];

      if (!Array.isArray(modules)) {
        return [];
      }

      return modules.filter((module) => module?.fee_type === "other_fee");
    } catch (error) {
      return [];
    }
  };

  const getOtherFeeAmount = (item) => {
    const modules = getModules(item);

    if (modules.length) {
      return modules.reduce(
        (sum, module) => sum + Number(module?.amount || 0),
        0,
      );
    }

    return Number(item?.amount ?? item?.paid ?? 0);
  };

  const totalAmount = transactions.reduce(
    (sum, item) => sum + getOtherFeeAmount(item),
    0,
  );

  const uniqueStudents = new Set(
    transactions
      .map((item) => {
        return (
          item?.student?.id ||
          item?.student?.registration_no ||
          item?.library_student?.id ||
          item?.library_student?.student_id ||
          item?.hostel_student?.id ||
          item?.hostel_student?.student_id ||
          item?.student_id ||
          item?.registration_no
        );
      })
      .filter(Boolean),
  ).size;

  const getStudentName = (item) =>
    item?.student?.name ??
    item?.library_student?.student_name ??
    item?.hostel_student?.student_name ??
    item?.student_name ??
    "-";

  const getRegistrationNo = (item) =>
    item?.student?.registration_no ??
    item?.library_student?.registration_no ??
    item?.hostel_student?.registration_no ??
    item?.registration_no ??
    "-";

  const getPhone = (item) =>
    item?.student?.phone ??
    item?.library_student?.phone ??
    item?.hostel_student?.phone ??
    item?.phone ??
    "";

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      style={{
        backgroundColor: "rgba(15, 23, 42, 0.65)",
        backdropFilter: "blur(3px)",
      }}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
        role="document"
        style={{ maxWidth: "1250px" }}
      >
        <div
          className="modal-content border-0 shadow-lg rounded-4 overflow-hidden"
          style={{ maxHeight: "90vh" }}
        >
          <div className="modal-header border-bottom px-4 py-3">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center"
                style={{
                  width: 46,
                  height: 46,
                  background: "#8b5cf615",
                  color: "#8b5cf6",
                  flexShrink: 0,
                }}
              >
                <i className="ri-wallet-3-line fs-4" />
              </div>

              <div>
                <h5 className="modal-title fw-bold mb-0">
                  Other Fee Transactions
                </h5>

                <small className="text-muted">
                  Transactions loaded with the Other Fee type filter
                </small>
              </div>
            </div>

            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>

          <div className="modal-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div
                  className="spinner-border text-primary mb-3"
                  role="status"
                />

                <h6 className="fw-semibold">
                  Loading other fee transactions...
                </h6>

                <p className="text-muted mb-0">
                  Please wait while we fetch the fee list.
                </p>
              </div>
            ) : error ? (
              <div className="text-center py-5 px-4">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
                  style={{
                    width: 64,
                    height: 64,
                    background: "#fee2e2",
                    color: "#dc2626",
                  }}
                >
                  <i className="ri-error-warning-line fs-2" />
                </div>

                <h6 className="fw-bold">Unable to load transactions</h6>

                <p className="text-muted mb-3">{error}</p>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onRetry}
                >
                  <i className="ri-refresh-line me-1" />
                  Retry
                </button>
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-5 px-4">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
                  style={{
                    width: 70,
                    height: 70,
                    background: "#f1f5f9",
                    color: "#64748b",
                  }}
                >
                  <i className="ri-wallet-3-line fs-2" />
                </div>

                <h6 className="fw-bold mb-1">No Other Fee Transactions</h6>

                <p className="text-muted mb-0">
                  No transactions were found for the Other Fee type.
                </p>
              </div>
            ) : (
              <>
                <div className="p-3 border-bottom bg-light">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="card border-0 shadow-sm rounded-3 h-100">
                        <div className="card-body py-3">
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <small className="text-muted d-block">
                                Total Transactions
                              </small>

                              <h5 className="fw-bold mb-0 mt-1">
                                {transactions.length.toLocaleString("en-IN")}
                              </h5>
                            </div>

                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: 40,
                                height: 40,
                                background: "#e0e7ff",
                                color: "#4f46e5",
                              }}
                            >
                              <i className="ri-file-list-3-line fs-5" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="card border-0 shadow-sm rounded-3 h-100">
                        <div className="card-body py-3">
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <small className="text-muted d-block">
                                Total Amount
                              </small>

                              <h5 className="fw-bold text-success mb-0 mt-1">
                                {inr(totalAmount)}
                              </h5>
                            </div>

                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: 40,
                                height: 40,
                                background: "#dcfce7",
                                color: "#16a34a",
                              }}
                            >
                              <i className="ri-money-rupee-circle-line fs-5" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="card border-0 shadow-sm rounded-3 h-100">
                        <div className="card-body py-3">
                          <div className="d-flex align-items-center justify-content-between">
                            <div>
                              <small className="text-muted d-block">
                                Students
                              </small>

                              <h5 className="fw-bold mb-0 mt-1">
                                {uniqueStudents.toLocaleString("en-IN")}
                              </h5>
                            </div>

                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: 40,
                                height: 40,
                                background: "#f3e8ff",
                                color: "#9333ea",
                              }}
                            >
                              <i className="ri-group-line fs-5" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="px-4" style={{ minWidth: 55 }}>
                          #
                        </th>
                        <th style={{ minWidth: 220 }}>
                          <i className="ri-user-3-line me-1" />
                          Student
                        </th>
                        <th style={{ minWidth: 130 }}>
                          <i className="ri-file-list-2-line me-1" />
                          Receipt
                        </th>
                        <th style={{ minWidth: 220 }}>
                          <i className="ri-wallet-3-line me-1" />
                          Other Fee
                        </th>
                        <th style={{ minWidth: 120 }}>
                          <i className="ri-money-rupee-circle-line me-1" />
                          Amount
                        </th>
                        <th style={{ minWidth: 140 }}>
                          <i className="ri-bank-card-line me-1" />
                          Payment
                        </th>
                        <th style={{ minWidth: 160 }}>
                          <i className="ri-bank-line me-1" />
                          Bank / Account
                        </th>
                        <th style={{ minWidth: 130 }}>
                          <i className="ri-calendar-line me-1" />
                          Payment Date
                        </th>
                        <th style={{ minWidth: 180 }}>
                          <i className="ri-time-line me-1" />
                          Created
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {transactions.map((item, index) => {
                        const payMeta = getPaymentMethodMeta(
                          item?.payment_method,
                        );
                        const modules = getModules(item);
                        const otherFeeAmount = getOtherFeeAmount(item);
                        const studentName = getStudentName(item);
                        const registrationNo = getRegistrationNo(item);
                        const phone = getPhone(item);

                        return (
                          <tr key={item?.id ?? `other-${index}`}>
                            <td className="px-4">
                              <span className="text-muted fw-medium">
                                {index + 1}
                              </span>
                            </td>

                            <td>
                              <div className="d-flex align-items-start gap-2">
                                <div
                                  className="rounded-circle d-flex align-items-center justify-content-center"
                                  style={{
                                    width: 34,
                                    height: 34,
                                    background: "#f1f5f9",
                                    color: "#64748b",
                                    flexShrink: 0,
                                  }}
                                >
                                  <i className="ri-user-line" />
                                </div>

                                <div style={{ minWidth: 0 }}>
                                  <div
                                    className="fw-semibold text-truncate"
                                    style={{ maxWidth: 170 }}
                                    title={studentName}
                                  >
                                    {studentName}
                                  </div>

                                  <small className="text-muted d-block">
                                    <i className="ri-hashtag me-1" />
                                    {registrationNo}
                                  </small>

                                  {phone && (
                                    <small className="text-muted d-block">
                                      <i className="ri-phone-line me-1" />
                                      {phone}
                                    </small>
                                  )}
                                </div>
                              </div>
                            </td>

                            <td>
                              <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2">
                                {item?.receipt_no || "-"}
                              </span>

                              {item?.transaction_no && (
                                <small className="text-muted d-block mt-1 font-monospace">
                                  {item.transaction_no}
                                </small>
                              )}
                            </td>

                            <td>
                              {modules.length ? (
                                modules.map((module, moduleIndex) => (
                                  <div
                                    key={moduleIndex}
                                    className="d-flex align-items-center justify-content-between gap-2 mb-1"
                                  >
                                    <span className="badge bg-light text-dark border fw-medium">
                                      {module?.type || "Other Fee"}
                                    </span>

                                    <strong className="text-success">
                                      {inr(module?.amount)}
                                    </strong>
                                  </div>
                                ))
                              ) : (
                                <span className="badge bg-light text-dark border fw-medium">
                                  Other Fee
                                </span>
                              )}
                            </td>

                            <td>
                              <strong className="text-success">
                                {inr(otherFeeAmount)}
                              </strong>
                            </td>

                            <td>
                              <span
                                className={`badge rounded-pill ${payMeta.cls}`}
                              >
                                <i className={`${payMeta.icon} me-1`} />
                                {item?.payment_method || "-"}
                              </span>
                            </td>

                            <td>
                              <div className="fw-medium">
                                <i className="ri-bank-line me-1 text-muted" />
                                {item?.bank?.owner_name ||
                                  item?.bank?.account_no ||
                                  "-"}
                              </div>

                              <small className="text-muted">
                                {item?.account_no || "-"}
                              </small>
                            </td>

                            <td>
                              <span className="fw-medium">
                                {formatDate(item?.payment_date)}
                              </span>
                            </td>

                            <td>
                              <small className="text-muted d-block">
                                {formatDateTime(item?.created_at)}
                              </small>

                              {item?.created_by?.name && (
                                <small className="text-muted d-block mt-1">
                                  <i className="ri-user-line me-1" />
                                  {item.created_by.name}
                                </small>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer border-top">
            <button
              type="button"
              className="btn btn-light border"
              onClick={onClose}
            >
              <i className="ri-close-line me-1" />
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
